import React, { useState } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import "../styles.css";

import CustomSnackbar from "../components/CustomSnackbar";
import ApprovedOrdersHistory from "../components/ApproveLines/ApprovedOrdersHistory";
import OrderCard from "../components/ApproveLines/OrderCard";
import OrderCardMobile from "../components/ApproveLines/OrderCardMobile";
import HeaderTabs from "../components/HeaderTabs";

import { useUser } from "../contexts/UserContext";
import useApproveLines from "./ApproveLines/hooks/useApproveLines";
import useOrderFiltering from "./ApproveLines/hooks/useOrderFiltering";
import OrderFilters from "./ApproveLines/components/OrderFilters";
import OrderFiltersMobile from "./ApproveLines/components/OrderFiltersMobile";
import { handleExportOrdersToExcel } from "./ApproveLines/utils/orderExport";

const ApproveLinesPage = () => {
  const { instance } = useMsal();
  const { user } = useUser();
  const [tabValue, setTabValue] = useState(0);

  const {
    ordersList,
    loading,
    serialNumbers,
    snackbar,
    setSnackbar,
    handleQuantityChange,
    handleLineCheckboxChange,
    handleDeltaCheckboxChange,
    handleConfirmCheckedLines,
    handleToggleReviewed,
    fetchSerialNumbers,
    updateOrderData,
  } = useApproveLines(tabValue);

  const {
    searchTerm,
    setSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    reviewedFilter,
    setReviewedFilter,
    filteredOrders,
  } = useOrderFiltering(ordersList);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getOrderIndex = (order) => {
    return ordersList.findIndex((o) => o._uniqueId === order._uniqueId);
  };

  return (
    <div className="App">
      <Box
        flexDirection="column"
        gap={4}
        p={2}
        sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}
      >
        <HeaderTabs
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          labels={["אישור הזמנות ", " הסטוריית אישורים"]}
        />
        
        {tabValue === 0 ? (
          <Box display="flex" flexDirection="column" gap={4} p={2}>
            {/* Desktop View */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              {!loading && (
                <OrderFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  onExport={() => handleExportOrdersToExcel(filteredOrders)}
                  hasOrders={filteredOrders && filteredOrders.length > 0}
                  reviewedFilter={reviewedFilter}
                  setReviewedFilter={setReviewedFilter}
                />
              )}

              {loading && (
                <Box>
                  <LinearProgress sx={{ marginTop: 2 }} />
                  <Typography variant="body1" mt={2}>
                    אנא המתן...
                  </Typography>
                </Box>
              )}

              {!loading && filteredOrders.length === 0 && (
                <Typography mt={2}>לא נמצאו הזמנות</Typography>
              )}

              {!loading &&
                filteredOrders.length > 0 &&
                filteredOrders.map((order) => {
                  const orderIndex = getOrderIndex(order);
                  return (
                    <OrderCard
                      key={order._uniqueId}
                      order={order}
                      handleConfirmcheckedLines={handleConfirmCheckedLines}
                      orderIndex={orderIndex}
                      handleQuantityChange={handleQuantityChange}
                      handleDeltaCheckboxChange={handleDeltaCheckboxChange}
                      handleLineCheckboxChange={handleLineCheckboxChange}
                      fetchSerialNumbers={fetchSerialNumbers}
                      serialNumbers={serialNumbers}
                      updateOrderData={updateOrderData}
                      onToggleReviewed={handleToggleReviewed}
                    />
                  );
                })}

              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
              />
            </Box>

            {/* Mobile View */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              {!loading && (
                <OrderFiltersMobile
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  onExport={() => handleExportOrdersToExcel(filteredOrders)}
                  reviewedFilter={reviewedFilter}
                  setReviewedFilter={setReviewedFilter}
                />
              )}

              {loading && <LinearProgress />}

              {!loading && filteredOrders.length === 0 && (
                <Typography mt={2}>לא נמצאו הזמנות</Typography>
              )}

              {!loading &&
                filteredOrders.length > 0 &&
                filteredOrders.map((order) => {
                  const orderIndex = getOrderIndex(order);
                  return (
                    <OrderCardMobile
                      key={order._uniqueId}
                      order={order}
                      orderIndex={orderIndex}
                      handleQuantityChange={handleQuantityChange}
                      handleLineCheckboxChange={handleLineCheckboxChange}
                      handleDeltaCheckboxChange={handleDeltaCheckboxChange}
                      serialNumbers={serialNumbers}
                      handleConfirmcheckedLines={handleConfirmCheckedLines}
                      updateOrderData={updateOrderData}
                      fetchSerialNumbers={fetchSerialNumbers}
                      onToggleReviewed={handleToggleReviewed}
                    />
                  );
                })}

              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
              />
            </Box>
          </Box>
        ) : (
          <ApprovedOrdersHistory email={user.email} />
        )}
      </Box>
    </div>
  );
};

export default ApproveLinesPage;